import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check } from "lucide-react";

export function NotificationBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const load = () => api.get("/notifications").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    // Real-time push via WebSocket (fallback: the 20s poll above keeps working if WS fails).
    let ws;
    try {
      const token = localStorage.getItem("sj_token");
      if (token) {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
        ws = new WebSocket(`${base}/api/ws?token=${token}`);
        ws.onmessage = () => load();
      }
    } catch { /* fallback to polling */ }
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); try { ws && ws.close(); } catch {} };
  }, []);

  const openItem = async (n) => {
    if (!n.read) { await api.put(`/notifications/${n.id}/read`); load(); }
    setOpen(false);
    if (n.link) {
      if (n.link.startsWith("http")) {
        const path = n.link.split(window.location.origin)[1] || n.link;
        navigate(path);
      } else navigate(n.link);
    }
  };
  const markAll = async () => { await api.put("/notifications/read-all"); load(); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={() => setOpen(!open)} className="relative text-slate-600 hover:text-slate-900 transition-colors">
        <Bell style={{ width: 20, height: 20 }} />
        {data.unread > 0 && <span data-testid="notification-count" className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="font-semibold text-sm text-slate-900">Notifications</span>
            {data.unread > 0 && <button data-testid="mark-all-read" onClick={markAll} className="text-xs text-blue-600 flex items-center gap-1"><Check className="w-3 h-3" /> Mark all read</button>}
          </div>
          {data.notifications.length === 0 ? <p className="px-4 py-8 text-center text-sm text-slate-400">No notifications yet.</p> : data.notifications.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 ${!n.read ? "bg-blue-50/50" : ""}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 shrink-0" />}
                <div className="min-w-0"><div className="text-sm font-medium text-slate-900">{n.title}</div><div className="text-xs text-slate-500">{n.body}</div><div className="text-[10px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</div></div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
